import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Stack,
  MenuItem,
} from '@mui/material';
import { useGetTemplates } from '@frontend/features/whatsapp/hooks/useGetTemplates';
import { useTranslation } from 'react-i18next';

type TemplateConfigDialogProps = {
  open: boolean;
  agencyId: number;
  initialTemplateId?: string;
  initialParams?: string[];
  onClose: () => void;
  onSave: (data: { templateId: string; params: string[]; messageText?: string }) => void;
};

const replacePlaceholders = (template: string, values: string[]) => {
  return template.replace(/{{(\d+)}}/g, (_match, index) => {
    const i = parseInt(index, 10) - 1;
    return values[i] ?? '';
  });
};

export const TemplateConfigDialog = ({
  open,
  agencyId,
  initialTemplateId,
  initialParams = [],
  onClose,
  onSave,
}: TemplateConfigDialogProps) => {
  const { data, isLoading } = useGetTemplates(agencyId);
  const [templateId, setTemplateId] = useState<string>(initialTemplateId || '');
  const [params, setParams] = useState<string[]>(initialParams);
  const [searchText, setSearchText] = useState('');
  const { t } = useTranslation();

  const templates = data?.templates || [];
  const selectedTemplate = useMemo(() => templates.find((tpl) => tpl.id === templateId), [templateId, templates]);
  const enabledTemplates = useMemo(
    () => templates.filter((tpl) => tpl.enabled !== false),
    [templates],
  );
  const filteredTemplates = useMemo(() => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return enabledTemplates;
    return enabledTemplates.filter((tpl) => {
      const name = tpl.elementName || '';
      const id = tpl.id || '';
      return `${name} ${id}`.toLowerCase().includes(searchValue);
    });
  }, [enabledTemplates, searchText]);
  const templateOptions = useMemo(() => {
    if (selectedTemplate?.enabled === false) {
      return [selectedTemplate, ...filteredTemplates.filter((tpl) => tpl.id !== selectedTemplate.id)];
    }
    return filteredTemplates;
  }, [selectedTemplate, filteredTemplates]);

  useEffect(() => {
    if (open) {
      setTemplateId(initialTemplateId || '');
      setParams(initialParams || []);
      setSearchText('');
    }
  }, [open, initialTemplateId, initialParams]);

  useEffect(() => {
    const placeholders = selectedTemplate?.data.match(/{{\d+}}/g) || [];
    const nextParams = [...params];
    if (placeholders.length !== params.length) {
      const resized = Array.from({ length: placeholders.length }, (_, idx) => params[idx] || '');
      setParams(resized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate]);

  const handleParamChange = (index: number, value: string) => {
    setParams((prev) => {
      const copy = [...prev];
      copy[index] = value;
      return copy;
    });
  };

  const handleSave = () => {
    if (!templateId) return;
    const messageText = selectedTemplate ? replacePlaceholders(selectedTemplate.data, params) : undefined;
    onSave({ templateId, params, messageText });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Select template</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            fullWidth
            label={t('common.search')}
            placeholder={t('common.search')}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <TextField
            select
            fullWidth
            label="Template"
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={isLoading}
          >
            <MenuItem value="">Select template</MenuItem>
            {templateOptions.map((tpl) => (
              <MenuItem key={tpl.id} value={tpl.id}>
                {tpl.elementName || tpl.id}
                {tpl.enabled === false ? ' (disabled)' : ''}
              </MenuItem>
            ))}
          </TextField>

          {selectedTemplate && (
            <Stack spacing={1}>
              <Typography variant="subtitle2">Template text</Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {selectedTemplate.data}
              </Typography>
              {params.map((value, idx) => (
                <TextField
                  key={idx}
                  label={`Param ${idx + 1}`}
                  value={value}
                  onChange={(e) => handleParamChange(idx, e.target.value)}
                  fullWidth
                />
              ))}
              {params.length === 0 && <Typography variant="caption">No parameters required</Typography>}
              {params.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Preview: {replacePlaceholders(selectedTemplate.data, params)}
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                {t('caseTriggers.template.placeholdersHint')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('caseTriggers.template.supportedPlaceholders')}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!templateId}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
